/**
 * Extracts a human-readable message from an Axios error response.
 *
 * Handles express-validator's `{ errors: [{ msg, path }] }` shape AND the
 * service-layer `{ errors: ['Recipient address is required', ...] }` plain-string
 * shape (both HTTP 400). The backend has no i18n and its prose is not a UI
 * contract, therefore unknown messages and codes always resolve to the caller's
 * Polish fallback rather than being displayed verbatim. The fallback must name
 * the failed operation and, when available, the affected business document so
 * a screenshot is still useful without exposing backend prose.
 *
 * The backend has no i18n, so its validation strings arrive in English. Pass an
 * optional `messageMap` (stable backend code or known legacy validation string
 * -> i18n key) to localize known messages.
 *
 * `fallbackParams` interpolates the fallback key so that even the "server said
 * nothing useful" path can still name the object the operator was working on
 * ("Nie udało się zapisać dokumentu PZ/55/08/2026"). See `utils/feedback.ts` for
 * the copy policy this supports.
 */
export function getApiErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  messageMap?: Record<string, string>,
  fallbackParams?: Record<string, unknown>
): string {
  const data = error?.response?.data;
  if (data) {
    if (typeof data.code === 'string' && messageMap?.[data.code]) {
      return t(messageMap[data.code]);
    }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const messages = data.errors
        .map((e: any) => (typeof e === 'string' ? e : e?.msg))
        .filter((m: any): m is string => typeof m === 'string' && m.length > 0)
        .map((m: string) => (messageMap && messageMap[m] ? t(messageMap[m]) : null))
        .filter((m: string | null): m is string => m !== null);
      if (messages.length > 0) return messages.join('; ');
    }
    if (typeof data.message === 'string' && messageMap?.[data.message]) {
      return t(messageMap[data.message]);
    }
    if (typeof data.error === 'string' && messageMap?.[data.error]) {
      return t(messageMap[data.error]);
    }
  }
  return fallbackParams ? t(fallbackKey, fallbackParams) : t(fallbackKey);
}
